// The Monthly page's filter tabs (Mismatch, Switch Back, Expired Cars, Booking Expired, Late Payment, Stale KM,
// No Rent, Unpaid) from the open contracts, the maintenance log and the fleet. Pure - no worker, no React: the
// Contracts worker runs it, and contractsStats.test.js holds it to the lists the worker gave before (the old code is
// kept for that test in __fixtures__/contractsWorker.head.js).
//
// Speed (owner, 28 Sep 2026: "the Monthly page takes about 4 seconds, the Airport page is instant"): the maintenance
// log and the fleet are read ONCE per run, into an index by plate. Every contract used to filter all the logs
// (~1,500) and scan all the cars (~1,600), normalising each of their plates again, for each of its plates.
// The rules themselves are unchanged, line for line - but Mismatch and Switch Back, which follow utils/switchBack
// since 4 Nov 2026 (every contract, its own car ready on Ejar or back in the log after the switch).

// The balance, 'No rent' and the contract category are the page's own shared helpers (one rule for the
// table, the badges and these tabs - DUP-06 / CON-11 / CON-10); the rest are local helpers.
import { liveBalance, hasNoRent, isUnpaid } from '../utils/liveBalance';
import { contractCategory, noMoneyNoteOf } from '../utils/contractRows';
import { dubaiToday } from '../../../services/contractRules';
// Switch / Switch Back: the one rule of the tabs, the table's marks and the WhatsApp menus (owner, 4 Nov 2026)
import { inMismatch, switchBackOf } from '../utils/switchBack';

// The tabs' plate key: letters and digits (Arabic kept), so "Y 87918" and "y87918" are one car. (The table's own
// marks use utils/normalize, as before.)
const normalize = (s) => (s || '').toString().toLowerCase().trim().replace(/[^a-z0-9؀-ۿ]/g, '');

const parseNumber = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    return parseFloat(val.toString().replace(/,/g, '')) || 0;
};

const parseSheetDate = (dateStr) => {
    if (!dateStr || (typeof dateStr !== 'string' && typeof dateStr !== 'number')) return null;
    const str = dateStr.toString().replace(/\//g, '-');
    const parts = str.split(/[-/\s:]/);
    if (parts.length < 3) return null;
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10) - 1;
    let year = parseInt(parts[2], 10);
    if (year < 100) year += 2000;
    const d = new Date(Date.UTC(year, month, day));
    return isNaN(d.getTime()) ? null : d;
};

const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Handle DD/MM/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }
    return new Date(dateStr);
};

const NO_LOGS = [];

/** The maintenance log by plate, built once per run. `logsOf(plate)`: that car's logs, in the log's order. */
export const indexMaintenance = (maintenanceData) => {
    const byPlate = new Map();
    (maintenanceData || []).forEach((m) => {
        const key = normalize(m["Vehicle"]);
        const logs = byPlate.get(key);
        if (logs) logs.push(m); else byPlate.set(key, [m]);
    });
    return {
        logsOf: (plate) => byPlate.get(normalize(plate)) || NO_LOGS,
    };
};

/** A car's registration (or its insurance) ended before `today`. */
const carExpiredOn = (car, today) => {
    if (car.registration_expiry) {
        const regDate = parseDate(car.registration_expiry);
        if (regDate && regDate < today) return true;
        // Company rule: the insurance always ends one month AFTER the registration, so
        // it is derived from it; a stored insurance date older than that is stale.
        if (regDate) {
            const ins = new Date(regDate); ins.setMonth(ins.getMonth() + 1);
            return ins < today;
        }
    }
    if (car.insurance_expiry) {
        const insDate = parseDate(car.insurance_expiry);
        if (insDate && insDate < today) return true;
    }
    return false;
};

/**
 * The fleet by plate, built once per run: a plate finds the FIRST car (the fleet's order) whose plate_number or
 * plate_no reads the same - what the old scan found. `isExpired(plate)`: that car's registration / insurance ended
 * before `now`; each car is worked out once. `statusOf(plate)`: its Ejar status now ('' when not in the fleet).
 */
export const indexCars = (carsData, now = new Date()) => {
    const byPlate = new Map();
    (carsData || []).forEach((c) => {
        if (!c) return;
        [normalize(c.plate_number), normalize(c.plate_no)].forEach((key) => { if (!byPlate.has(key)) byPlate.set(key, c); });
    });
    const expired = new Map();
    const count = (carsData || []).length;
    return {
        isExpired(plateNo) {
            if (!plateNo || !count) return false;
            const car = byPlate.get(normalize(plateNo));
            if (!car) return false;
            if (!expired.has(car)) expired.set(car, carExpiredOn(car, now));
            return expired.get(car);
        },
        statusOf(plateNo) {
            const car = plateNo ? byPlate.get(normalize(plateNo)) : null;
            return (car && car.status) || '';
        },
    };
};

/** What the Switch Back rule reads (utils/switchBack): each car's maintenance logs and its Ejar status. */
export const switchBackLookups = (maintenanceData, carsData) => ({
    logsOf: indexMaintenance(maintenanceData).logsOf,
    statusOf: indexCars(carsData).statusOf,
});

/**
 * The eight tab lists, each in the open contracts' order. `today` (the Dubai calendar day - CON-24) and `now`
 * (for the fleet's expiry dates) are taken once per run.
 */
export const computeContractStats = ({ openContracts, maintenanceData, carsData }, { today = dubaiToday(), now = new Date() } = {}) => {
    const cars = indexCars(carsData, now);
    const switchLookups = { logsOf: indexMaintenance(maintenanceData).logsOf, statusOf: cars.statusOf, today };

    const mismatch = [];
    const switchback = [];
    const expired = [];
    const bookingExp = [];
    const latePayment = [];
    const staleKm = [];
    const noRent = [];
    const unpaid = [];

    (openContracts || []).forEach(row => {
        // Mismatch: every contract on another car than its own while that car can come back; Switch Back: it is ready
        // (utils/switchBack)
        if (inMismatch(row, switchLookups)) {
            mismatch.push(row);
            if (switchBackOf(row, switchLookups)) switchback.push(row);
        }

        // Expired (RTA)
        const plates = [row.ejarPlate, row.invygoPlate].filter(Boolean);
        if (plates.some(p => cars.isExpired(p))) {
            expired.push(row);
        }

        // Booking Expiry / Returns (Include all for calendar visibility)
        const dropoff = parseSheetDate(row.dropoffDate);
        if (dropoff) {
            bookingExp.push(row);
        }

        // --- Late Payment & Stale KM (Penalty Logic) ---
        if (row.type === 'open') {
            const monthlyItems = row.monthly_calculations || [];

            // Missing MO RENT (all open contracts EXCEPT Invygo: D04 / "Business Bay - Invygo" contracts
            // have no rent here - their account is settled with management). The table's red
            // 'No rent' badge reads the same helper (CON-11).
            if (hasNoRent(row)) noRent.push(row);

            // Late payment / stale KM: monthly contracts only - the one category rule of the page
            // (monthly from 30 days, Invygo and Leasing apart - CON-10); a row whose period is unknown
            // counts only when its stored type says monthly.
            const category = contractCategory(row);
            const isMonthly = category === 'Monthly' ||
                (!category && String(row.contractType || '').toLowerCase() === 'monthly');

            if (isMonthly) {
                const today0 = new Date(today);

                // 1. Late Payment (7 days after start of latest month if unpaid)
                let startDate = null;
                let rentDue = 0;
                let paid = 0;

                if (monthlyItems.length > 0) {
                    const lastMonth = monthlyItems[monthlyItems.length - 1];
                    startDate = parseSheetDate(lastMonth.start);
                    rentDue = parseNumber(lastMonth.totalRent);
                    paid = parseNumber(lastMonth.paid);
                } else if (row.pickupDate) {
                    startDate = parseSheetDate(row.pickupDate);
                    rentDue = parseNumber(row.total_rent || row['Total Rent'] || row.rent || row.Rent);
                    paid = parseNumber(row.paid || row.Paid); // No calculations saved = fallback
                }

                const netBal = liveBalance(row, today);

                // an Invygo or company contract is settled with the accountant: never "late" (owner, 1 Oct 2026)
                if (startDate && rentDue > paid && netBal > 0 && !noMoneyNoteOf(row)) {
                    const diffDays = Math.floor((today0 - startDate) / (1000 * 60 * 60 * 24));
                    if (diffDays > 7) latePayment.push(row);
                }

                // 2. Stale KM (40 days since last update)
                let lastUpdateDate = null;
                const lastKmUpdate = [...monthlyItems].reverse().find(m => parseFloat(m.currentKM) > 0);

                if (lastKmUpdate) {
                    lastUpdateDate = parseSheetDate(lastKmUpdate.end);
                } else if (row.pickupDate) {
                    lastUpdateDate = parseSheetDate(row.pickupDate);
                }

                if (lastUpdateDate) {
                    const diffDays = Math.floor((today0 - lastUpdateDate) / (1000 * 60 * 60 * 24));
                    if (diffDays > 40) staleKm.push(row);
                }
            }
        }
        // Unpaid: an open contract that owes (the live balance, the same as the table's column) and
        // has no payment in any month row.
        if (row.type === 'open' && isUnpaid(row, today)) {
            unpaid.push(row);
        }
    });

    return { mismatch, switchback, expired, bookingExp, latePayment, staleKm, noRent, unpaid };
};
